<template>
  <div class="calculators-view-container">
    <MainHeader :title="'Calculators'" :showBack="true" />

    <div class="view content content-padding">
      <!-- Intro hero -->
      <div class="calc-hero">
        <div class="calc-hero-icon">
          <font-awesome-icon :icon="['fas', 'calculator']" />
        </div>
        <div class="calc-hero-text">
          <p class="calc-hero-title">Plan with confidence</p>
          <p class="calc-hero-subtitle">
            Run the numbers on debt, savings, and big decisions.
          </p>
        </div>
      </div>

      <div v-for="group in groups" :key="group.label" class="calc-section">
        <span class="section-label">{{ group.label }}</span>

        <div class="calc-card">
          <div
            v-for="(calc, idx) in group.calculators"
            :key="calc.routeName"
            class="calc-row"
            :class="{ 'is-last': idx === group.calculators.length - 1 }"
            @click="openCalculator(calc)"
          >
            <div class="calc-icon-wrap" :class="calc.tone">
              <font-awesome-icon :icon="['fas', calc.icon]" class="calc-icon" />
            </div>
            <div class="calc-text">
              <p class="calc-title">
                {{ calc.title }}
                <span v-if="calc.badge" class="calc-badge">{{
                  calc.badge
                }}</span>
              </p>
              <p class="calc-description">{{ calc.description }}</p>
            </div>
            <font-awesome-icon
              :icon="['fas', 'chevron-right']"
              class="calc-chevron"
            />
          </div>
        </div>
      </div>

      <p class="calc-footnote">
        Estimates are for planning only and don't constitute financial advice.
      </p>
    </div>
  </div>
</template>

<script>
import { useRouter } from 'vue-router';
import MainHeader from '../../../components/View-Header.vue';
import { onMounted } from 'vue';

export default {
  components: { MainHeader },
  setup() {
    const router = useRouter();

    const groups = [
      {
        label: 'Debt',
        calculators: [
          {
            title: 'Debt Payoff',
            description:
              'Compare avalanche, snowball, and custom strategies across all your debts.',
            icon: 'credit-card',
            tone: 'coral',
            routeName: 'DebtPayoff',
          },
          //   {
          //     title: 'Loan Planner',
          //     description:
          //       'See how extra payments shrink your timeline and total interest.',
          //     icon: 'sack-dollar',
          //     tone: 'purple',
          //     routeName: 'LoanPlanner',
          //   },
          {
            title: 'Mortgage Payoff',
            description:
              'Run amortization scenarios with extra principal and refinance options.',
            icon: 'house',
            tone: 'teal',
            routeName: 'Mortgage',
          },
        ],
      },
      {
        label: 'Saving & Planning',
        calculators: [
          {
            title: 'Savings Goal',
            description:
              'Hit a target by a date — find the per-paycheck amount you need.',
            icon: 'piggy-bank',
            tone: 'green',
            routeName: 'SavingsGoal',
          },
          {
            title: 'Compound Interest',
            description:
              'Project growth over time with contributions and a return rate.',
            icon: 'chart-line',
            tone: 'purple',
            routeName: 'CompoundInterest',
          },
        ],
      },
      {
        label: 'Retirement',
        calculators: [
          {
            title: 'Retirement Planner',
            description:
              'Traditional retirement projection based on age, savings, and contributions.',
            icon: 'umbrella-beach',
            tone: 'blue',
            routeName: 'Retirement',
          },
          {
            title: 'FIRE Calculator',
            description:
              'Find your FIRE number plus Lean, Fat, and Coast variants.',
            icon: 'fire',
            tone: 'coral',
            badge: 'New',
            routeName: 'Fire',
          },
        ],
      },
      {
        label: 'Spending Decisions',
        calculators: [
          {
            title: 'Can I Afford This?',
            description:
              'Test a purchase against your budget and goal timelines.',
            icon: 'circle-question',
            tone: 'teal',
            routeName: 'Affordability',
          },
          {
            title: 'Paycheck Estimator',
            description:
              'Gross to net take-home, including tax and contribution changes.',
            icon: 'money-check-dollar',
            tone: 'green',
            routeName: 'Paycheck',
          },
          {
            title: 'Subscription Audit',
            description:
              'See what your recurring charges actually cost per year.',
            icon: 'arrows-rotate',
            tone: 'purple',
            routeName: 'SubscriptionAudit',
          },
        ],
      },
    ];

    const openCalculator = (calc) => {
      router.push({ name: calc.routeName });
    };

    onMounted(() => {
      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) mainElement.scrollTo(0, 0);
      }, 100);
    });

    return {
      groups,
      openCalculator,
    };
  },
};
</script>

<style scoped>
.calculators-view-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.content {
  flex-grow: 1;
}

/* Hero strip */
.calc-hero {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: linear-gradient(
    135deg,
    var(--purple-50) 0%,
    #f4f1ff 60%,
    #e9f7f1 100%
  );
  border-radius: var(--radius-lg);
  padding: 1rem 1.15rem;
  margin-bottom: 1.5rem;
  border: 1px solid #ece9fb;
  animation: cardSlideUp 0.4s cubic-bezier(0.34, 1.2, 0.64, 1) both;
}
.dark-theme .calc-hero {
  background: linear-gradient(135deg, #1e1a40 0%, #1a2535 60%, #0d2420 100%);
  border-color: #2a3a4e;
}

.calc-hero-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--gradient-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  flex-shrink: 0;
  box-shadow: var(--shadow-purple);
}

.calc-hero-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.calc-hero-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #004751;
  margin: 0;
}
.dark-theme .calc-hero-title {
  color: white;
}
.calc-hero-subtitle {
  font-size: 0.78rem;
  color: #6b7280;
  margin: 0;
}
.dark-theme .calc-hero-subtitle {
  color: #9aa6b8;
}

.calc-section {
  margin-bottom: 1.25rem;
}

.calc-card {
  background: white;
  border-radius: var(--radius-lg);
  border: 1px solid #f0f0f0;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  animation: cardSlideUp 0.4s cubic-bezier(0.34, 1.2, 0.64, 1) both;
}
.dark-theme .calc-card {
  background: #212e3e;
  border-color: #2a3a4e;
}

/* Rows */
.calc-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0.95rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid #f8f8f8;
  transition: background var(--transition-fast);
}
.dark-theme .calc-row {
  border-bottom-color: #2a3a4e;
}
.calc-row.is-last {
  border-bottom: none;
}
.calc-row:hover {
  background: #fafafa;
}
.dark-theme .calc-row:hover {
  background: #1a2535;
}

.calc-icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.calc-icon-wrap.purple {
  background: var(--purple-50);
}
.calc-icon-wrap.teal {
  background: var(--teal-50);
}
.calc-icon-wrap.blue {
  background: #eef4ff;
}
.calc-icon-wrap.coral {
  background: #fdeee7;
}
.calc-icon-wrap.green {
  background: #e8f5ee;
}
.dark-theme .calc-icon-wrap.purple {
  background: #1e1a40;
}
.dark-theme .calc-icon-wrap.teal {
  background: #0d2420;
}
.dark-theme .calc-icon-wrap.blue {
  background: #0d1a30;
}
.dark-theme .calc-icon-wrap.coral {
  background: #2e1d18;
}
.dark-theme .calc-icon-wrap.green {
  background: #0f2a1c;
}

.calc-icon {
  font-size: 15px;
  color: var(--color-accent-purple);
}
.calc-icon-wrap.teal .calc-icon {
  color: var(--color-accent-teal);
}
.calc-icon-wrap.blue .calc-icon {
  color: #3b82f6;
}
.calc-icon-wrap.coral .calc-icon {
  color: #d85a30;
}
.calc-icon-wrap.green .calc-icon {
  color: #2f9f6e;
}

/* Text block */
.calc-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.calc-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #213547;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.dark-theme .calc-title {
  color: white;
}

.calc-badge {
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-accent-teal);
  background: var(--teal-50);
  padding: 2px 6px;
  border-radius: 999px;
}
.dark-theme .calc-badge {
  background: #0d2420;
}

.calc-description {
  font-size: 0.76rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.35;
}
.dark-theme .calc-description {
  color: #9aa6b8;
}

.calc-chevron {
  font-size: 0.72rem;
  color: #ccc;
  flex-shrink: 0;
}
.dark-theme .calc-chevron {
  color: #4a5a6e;
}

.calc-footnote {
  text-align: center;
  font-size: 0.7rem;
  color: #aaa;
  padding: 0.5rem 1rem 1.25rem;
  margin: 0;
  line-height: 1.4;
}
.dark-theme .calc-footnote {
  color: #6b7e95;
}
</style>
